import { useEffect, useState } from "react";
import { Zap, Save, ToggleRight, Mail } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AdminAutomation() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/automation");
      setCfg(r.data);
    } catch {
      toast.error("Could not load automation settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/automation", patch);
      setCfg(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-12 text-center font-bold">Loading…</div>;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin · Automation</p>
        <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-3">
          <Zap className="w-8 h-8" strokeWidth={2.5}/> Automated emails
        </h1>
        <p className="text-base font-medium text-slate-600 mt-2">
          Toggle re-engagement and lifecycle emails on or off. Sweeps run every minute in the background.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Placeholders: <code>{"{{first_name}}"}</code>, <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>, <code>{"{{plan}}"}</code>
        </p>
      </header>

      <section className="nb-card p-6 mb-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" strokeWidth={2.5}/> Inactivity re-engagement
            </div>
            <div className="font-display text-2xl font-black mt-1">
              {cfg.inactivity_enabled ? `On — fires after ${cfg.inactivity_days} day${cfg.inactivity_days !== 1 ? "s" : ""}` : "Off"}
            </div>
          </div>
          <button
            data-testid="auto-inactivity-toggle"
            onClick={() => save({ inactivity_enabled: !cfg.inactivity_enabled })}
            disabled={busy}
            className={`nb-btn-primary inline-flex items-center gap-2 ${cfg.inactivity_enabled ? "!bg-emerald-300" : "!bg-white"}`}
          >
            <ToggleRight className="w-5 h-5" strokeWidth={2.5}/>
            {cfg.inactivity_enabled ? "Turn off" : "Turn on"}
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save({
              inactivity_days: Number(cfg.inactivity_days),
              inactivity_subject: cfg.inactivity_subject,
              inactivity_body_html: cfg.inactivity_body_html,
            });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold mb-2">Days of inactivity before sending (1–90)</label>
            <input
              data-testid="auto-inactivity-days"
              type="number" min={1} max={90}
              value={cfg.inactivity_days}
              onChange={(e) => setCfg({ ...cfg, inactivity_days: e.target.value })}
              className="nb-input max-w-[160px]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold mb-2">Subject</label>
            <input
              data-testid="auto-inactivity-subject"
              value={cfg.inactivity_subject}
              onChange={(e) => setCfg({ ...cfg, inactivity_subject: e.target.value })}
              maxLength={200}
              className="nb-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold mb-2">Body (HTML allowed)</label>
            <textarea
              data-testid="auto-inactivity-body"
              value={cfg.inactivity_body_html}
              onChange={(e) => setCfg({ ...cfg, inactivity_body_html: e.target.value })}
              rows={8}
              maxLength={4000}
              className="nb-input font-mono text-sm"
            />
          </div>
          <button type="submit" disabled={busy} data-testid="auto-inactivity-save" className="nb-btn-primary inline-flex items-center gap-2">
            <Save className="w-4 h-4" strokeWidth={2.5}/> Save
          </button>
        </form>
      </section>

      <p className="text-xs text-slate-500">
        Each user receives the inactivity email at most once per inactivity cycle; their counter resets when they sign in again. Users who opted out of digests are excluded automatically.
      </p>
    </div>
  );
}
